/* eslint-disable jsx-a11y/anchor-is-valid */
import {ErrorMessage, Field, Form, Formik, FormikProps} from 'formik'
import {FC, useState, useCallback, useContext, useEffect, useRef} from 'react'
import {DropBox} from '../components/dropzone/DropBox'
import {createStepSchema, ICreateStep, initsCreateStep} from '../../helpers/WizardHelpers'
import {CreateBatchRequest} from '../../../../../../redux/api/interfaces/batch'
import {useCreateBatchMutation} from '../../../../../../redux/api/services/batch'
import {WizardContext} from '../core/WizardContext'
import {DownloadTemplate} from '../components/dropzone/DownloadTemplate'
import {StepFooter} from '../components/footer/StepFooter'
import {useSelector} from 'react-redux'
import {RootState} from '../../../../../../redux/store'
import CustomAlert from '../../../../../../libs/_fri/components/CustomAlert'
import {CustomErrorAlert} from '../../../../../../libs/_fri/components/CustomErrorAlert/CustomErrorAlert'

const StepLoadFile: FC = () => {
  const [item, setItem] = useState<File>()
  const [errorFile, setErrorFile] = useState<boolean>(false)
  const [initialValues] = useState<ICreateStep>(initsCreateStep)
  const [createBatch, {isLoading, isError, error, isSuccess, reset}] = useCreateBatchMutation()
  const {nextStep} = useContext(WizardContext)
  const formRef = useRef<FormikProps<ICreateStep>>(null)
  const infoState = useSelector((state: RootState) => state.info)

  const onDrop = useCallback(
    (setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void) =>
      (acceptedFiles: File[]) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
          setErrorFile(false)
          setItem(acceptedFiles[0])
          setFieldValue('file', acceptedFiles[0])
        } else {
          setItem(undefined)
          setErrorFile(true)
        }
      },
    []
  )

  useEffect(() => {
    if (isSuccess) nextStep()

    formRef.current?.resetForm()
  }, [isSuccess])

  const handleSubmit = async (values: ICreateStep) => {
    if (!values.file) return

    const data: CreateBatchRequest = {
      body: {
        info: infoState,
        requestContent: {
          batchTitle: values.title,
          reference: values.reference,
          allowMissingUsers: values.allowMissingUsers,
        },
      },
      file: values.file,
    }
    createBatch(data)
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createStepSchema}
      onSubmit={handleSubmit}
      onReset={() => setItem(undefined)}
      innerRef={formRef}
    >
      {({setFieldValue, handleSubmit}) => {
        return (
          <div className='w-100'>
            <CustomAlert
              show={errorFile}
              className={'mb-8'}
              title='Error al cargar el archivo'
              text='El tamaño máximo para cargar archivos es de 10MB'
              variant={'warning'}
            />
            <CustomErrorAlert show={isError} error={error} close={reset} />

            <form>
              <label className='fw-semibold text-primary-dark fs-2x mb-6'>Subir archivo</label>
              <DropBox
                item={item}
                onRemoveItem={() => {
                  setItem(undefined)
                  setFieldValue('file', '')
                }}
                onDrop={onDrop(setFieldValue)}
              />
              <div className='text-danger mt-2'>
                <ErrorMessage name='file' />
              </div>

              <DownloadTemplate className={'pt-1 mb-15'} />

              <label className='fw-semibold text-primary-dark fs-2x mb-6'>Datos del pago</label>

              <div className='mb-9 fv-row'>
                <label className='form-label mb-3 text-gray fs-2'>Título del pago</label>
                <Field
                  name='title'
                  type='text'
                  placeholder='Ej: Pago área comercial'
                  className='form-control form-control-lg form-control-solid text-primary-dark fs-2'
                />
                <div className='text-danger mt-2'>
                  <ErrorMessage name='title' />
                </div>
              </div>
              <div className='mb-9 fv-row'>
                <label className='form-label mb-3 text-gray fs-2 required'>Referencia</label>
                <Field
                  name='reference'
                  type='text'
                  placeholder='Ej: Bono anual'
                  className='form-control form-control-lg form-control-solid text-primary-dark fs-2'
                />
                <div className='text-danger mt-2'>
                  <ErrorMessage name='reference' />
                </div>
              </div>
              <div className='fv-row'>
                <div className='form-check d-flex align-items-center'>
                  <Field
                    name='allowMissingUsers'
                    type='checkbox'
                    id='allowMissingUsers'
                    className='form-check-input'
                  />
                  <label
                    className='form-check-label text-gray fs-2 ms-2 fw-semibold'
                    htmlFor='allowMissingUsers'
                  >
                    Ignorar usuarios no existentes o bloqueados
                  </label>
                </div>

                <div className='text-danger mt-2'>
                  <ErrorMessage name='allowMissingUsers' />
                </div>
              </div>

              <StepFooter className={'pt-15'} onNext={handleSubmit} isLoading={isLoading} />
            </form>
          </div>
        )
      }}
    </Formik>
  )
}

export {StepLoadFile}
